'use client';

import dynamic from 'next/dynamic';

import { useMediaQueries } from '@commons/contexts/responsive';
import type {
  DrupalBlock,
  DrupalImage as DrupalImageType,
  DrupalParagraph,
} from '@commons/types/drupal';

/**
 * Lazy loading the variants to reduce the initial bundle size.
 *
 * This does not work when used in a pagebuilder factory directly, because every items are loaded at once.
 * Doing it in the component itself ensures that the chunks are only loaded when the component is rendered.
 *
 * @see https://nextjs.org/docs/app/building-your-application/optimizing/lazy-loading
 */

const TabbedContentBlockDesktop = dynamic(() => import('./Desktop'));
const TabbedContentBlockMobile = dynamic(() => import('./Mobile'));

/**
 * Type definition for a TabbedContent item.
 */

export type TabbedContentBlockItem = DrupalParagraph & {
  title: string;
  content: string;
  image?: DrupalImageType;
};

/**
 * Type definition for the TabbedContent component properties.
 */

export type TabbedContentBlockProps = DrupalBlock<{
  items: TabbedContentBlockItem[];
}>;

/**
 * TabbedContent block component.
 */

const TabbedContentBlock = (props: TabbedContentBlockProps) => {
  const { ready, minWidth } = useMediaQueries();

  const isMobile = ready && !minWidth.lg;

  if (isMobile) {
    return <TabbedContentBlockMobile {...props} />;
  }

  return <TabbedContentBlockDesktop {...props} />;
};

export default TabbedContentBlock;
